/**
 * How far along is a crew member in the assignment they're on right now?
 *
 * This is the domain half of the crew map marker: the ring around a
 * marker fills up as the current assignment progresses (empty at the
 * start, full when the shift is done). Rendering lives in
 * crewMarkerVisual.ts; the per-platform drawing code lives in the apps.
 *
 * Deliberately expressed in absolute epoch milliseconds rather than
 * "HH:MM" strings. Each app already knows how to turn a wall-clock
 * assignment into a real instant — the producer web app goes through
 * the briefing timezone (wallClockToUtc + effectiveShift helpers),
 * the crew app uses device-local time because the phone is on site.
 * Keeping this module free of that conversion means neither app has to
 * adopt the other's notion of "now".
 */

/** An assignment window as an absolute instant range. */
export type ShiftWindow = {
  startMs: number;
  endMs: number;
};

/** What the ring (and the "time left" label) should show. */
export type CrewShiftProgress = {
  /** 0 = just started, 1 = finished. 0 when there is no current shift. */
  progress: number;
  /** Whole minutes remaining, rounded up. 0 when there is no current shift. */
  minutesLeft: number;
};

const MS_PER_MINUTE = 60_000;

const NO_CURRENT_SHIFT: CrewShiftProgress = { progress: 0, minutesLeft: 0 };

const isUsableWindow = (w: ShiftWindow): boolean =>
  Number.isFinite(w.startMs) && Number.isFinite(w.endMs) && w.endMs >= w.startMs;

/**
 * The window the crew member is on at `nowMs`, or null when they're free.
 *
 * When windows overlap the latest-starting one wins — the same rule the
 * lockscreen tracker uses (see computeLiveTrackerState): the shoot that
 * started most recently is the one they're actually working.
 *
 * A window is "current" from its start up to (but not including) its
 * end, so a finished assignment immediately frees the marker instead of
 * leaving a full ring behind.
 */
export function selectCurrentShift(
  windows: readonly ShiftWindow[],
  nowMs: number,
): ShiftWindow | null {
  let current: ShiftWindow | null = null;
  for (const w of windows) {
    if (!isUsableWindow(w)) continue;
    if (w.startMs > nowMs || nowMs >= w.endMs) continue;
    if (!current || w.startMs > current.startMs) current = w;
  }
  return current;
}

/** Anything carrying a shift window — an assignment row, usually. */
export type WithShiftWindow = { window: ShiftWindow };

/**
 * Pick out what someone is on right now and what comes after it.
 *
 * Same rules as selectCurrentShift for "current"; "next" is the
 * earliest window that hasn't started yet. Returning both in one pass
 * keeps the crew list, the map popup and the crew app's detail sheet
 * from each inventing their own idea of "next".
 */
export function selectCurrentAndNext<T extends WithShiftWindow>(
  items: readonly T[],
  nowMs: number,
): { current: T | null; next: T | null } {
  let current: T | null = null;
  let next: T | null = null;

  for (const item of items) {
    const w = item.window;
    if (!isUsableWindow(w)) continue;

    if (w.startMs <= nowMs && nowMs < w.endMs) {
      if (!current || w.startMs > current.window.startMs) current = item;
      continue;
    }
    if (w.startMs > nowMs) {
      if (!next || w.startMs < next.window.startMs) next = item;
    }
  }

  return { current, next };
}

/**
 * Turn a window into ring progress + minutes left.
 *
 * Zero-length windows (start === end) count as finished rather than
 * dividing by zero — a shift with no duration has nothing left to run.
 */
export function computeShiftProgress(
  window: ShiftWindow | null | undefined,
  nowMs: number,
): CrewShiftProgress {
  if (!window || !isUsableWindow(window)) return NO_CURRENT_SHIFT;

  const duration = window.endMs - window.startMs;
  const elapsed = nowMs - window.startMs;
  const progress = duration <= 0 ? 1 : Math.min(1, Math.max(0, elapsed / duration));
  const minutesLeft = Math.max(0, Math.ceil((window.endMs - nowMs) / MS_PER_MINUTE));

  return { progress, minutesLeft };
}

/**
 * Human label for the time remaining, e.g. "42 min left", "1h 5m left".
 *
 * English on purpose: every string that reaches a screen in this product
 * is English, regardless of the team's working language.
 */
export function formatTimeLeft(minutesLeft: number): string {
  if (!Number.isFinite(minutesLeft) || minutesLeft <= 0) return "Ending now";
  if (minutesLeft < 60) return `${Math.round(minutesLeft)} min left`;

  const hours = Math.floor(minutesLeft / 60);
  const minutes = Math.round(minutesLeft % 60);
  return minutes === 0 ? `${hours}h left` : `${hours}h ${minutes}m left`;
}

/**
 * Human label for the wait until something starts, e.g. "in 42 min",
 * "in 1h 5m". The mirror of formatTimeLeft — kept beside it so a
 * countdown and a count-up never drift apart in phrasing.
 *
 * Rounds to whole minutes first: 119.7 minutes is "in 2h", never the
 * nonsensical "in 1h 60m".
 */
export function formatTimeUntil(minutesUntil: number): string {
  if (!Number.isFinite(minutesUntil)) return "Starting now";

  const total = Math.round(minutesUntil);
  if (total <= 0) return "Starting now";
  if (total < 60) return `in ${total} min`;

  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return minutes === 0 ? `in ${hours}h` : `in ${hours}h ${minutes}m`;
}
